import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import useTranslate from 'hooks/useTranslate';
import type { Agent } from 'types';

interface AgentRoleRowProps {
    label: string;
    agents: Agent[];
    currentId?: number | null;
    loading?: boolean;
    assigning?: boolean;
    disabled?: boolean;
    onAssign: (agentId: number) => void | Promise<boolean>;
}

const getAgentLabel = (agent: Agent | null | undefined): string => {
    if (!agent) return '';
    return `${agent.firstname ?? ''} ${agent.lastname ?? ''}`.trim();
};

const AgentRoleRow = ({
    label,
    agents,
    currentId,
    loading,
    assigning,
    disabled,
    onAssign,
}: AgentRoleRowProps) => {
    const { translate } = useTranslate();
    const options = Array.isArray(agents) ? agents : [];
    const selected = options.find((agent) => agent.id === currentId) ?? null;
    const isBusy = Boolean(loading || assigning);
    const noAgents = !loading && options.length === 0;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(110px, 140px) minmax(0, 1fr)' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 1.5 },
                px: 1.75,
                py: 1.5,
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'rgba(148, 163, 184, 0.18)',
                bgcolor: 'rgba(255, 255, 255, 0.92)',
            }}
        >
            <Typography
                variant="body2"
                sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}
            >
                {label}
            </Typography>

            {noAgents ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: 13, fontStyle: 'italic', lineHeight: 1.45 }}
                >
                    {translate('noAgentsAvailable')}
                </Typography>
            ) : (
                <Autocomplete
                    size="small"
                    fullWidth
                    options={options}
                    value={selected}
                    loading={isBusy}
                    disabled={disabled || isBusy}
                    disablePortal={false}
                    getOptionLabel={getAgentLabel}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    onChange={async (_event, agent) => {
                        if (!agent || agent.id === currentId) return;
                        await onAssign(agent.id);
                    }}
                    renderInput={(params) => (
                        <TextField {...params} placeholder={translate('selectAgent')} />
                    )}
                />
            )}
        </Box>
    );
};

export default AgentRoleRow;
